import React from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { GOLD, phaseSpoken, resolve } from "./showKit";
import { rise } from "./VisualsA";

const ease = [0.22, 1, 0.36, 1];
const num = (v, data) => Number(String(resolve(v, data)).replace(/[^0-9.-]/g, "")) || 0;

export function Split({ p, data }) {
  const total = num(p.total, data);
  const parts = p.parts.map(([l, v, c]) => [l, num(v, data), c]);
  return (
    <div className="w-full max-w-5xl space-y-8">
      <motion.div {...rise(0)} className="flex items-end justify-between">
        <span className="text-xl text-white/70">Paycheck</span>
        <span className="font-serif text-6xl md:text-7xl font-bold text-white">${total.toLocaleString()}</span>
      </motion.div>
      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.2, delay: 0.4, ease }} className="h-4 rounded-full origin-left" style={{ background: GOLD }} />
      <div className="space-y-4">
        {parts.map(([l, v, c], i) => (
          <motion.div key={l} {...rise(i + 2, 0.5)} className="grid grid-cols-[160px_1fr_110px] md:grid-cols-[240px_1fr_130px] items-center gap-4">
            <span className="text-lg md:text-2xl text-white">{i + 1}. {l}</span>
            <div className="h-10 rounded-xl bg-white/5 overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(3, (100 * v) / (total || 1))}%` }} transition={{ duration: 1.3, delay: 1.4 + i * 0.5, ease }} className="h-full rounded-xl" style={{ background: c, boxShadow: `0 0 30px ${c}66` }} /></div>
            <span className="text-right font-mono text-xl md:text-2xl text-white">${v.toLocaleString()}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function Ledger({ p }) {
  return (
    <div className="w-full max-w-4xl rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10"><span className="font-mono text-sm tracking-[0.3em] uppercase" style={{ color: GOLD }}>Append-only ledger</span><Lock className="w-5 h-5 text-white/60" /></div>
      {p.rows.map(([d, a], i) => (
        <motion.div key={i} initial={{ opacity: 0, x: -60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.5 + i * 0.7, ease }} className="flex items-center gap-4 px-6 py-4 border-b border-white/5">
          <span className="font-mono text-xs text-white/40 w-16">#{String(1040 + i).padStart(5, "0")}</span>
          <span className="flex-1 text-lg md:text-xl text-white">{d}</span>
          <span className={`font-mono text-lg md:text-xl ${String(a).startsWith("+") ? "text-emerald-300" : String(a).startsWith("−") ? "text-sky-300" : "text-amber-200"}`}>{a}</span>
          <motion.span initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9 + i * 0.7 }}><Lock className="w-4 h-4 text-white/40" /></motion.span>
        </motion.div>
      ))}
    </div>
  );
}

export function Chart({ p, data }) {
  const all = resolve(p.series, data) || [];
  const upto = p.upto ? num(p.upto, data) : all.length;
  const s = all.slice(0, Math.max(upto, 2));
  const max = Math.max(...all, 1);
  const W = 900, H = 380;
  const pts = s.map((v, i) => [40 + (i * (W - 60)) / Math.max(all.length - 1, 1), H - 30 - (v / max) * (H - 70)]);
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x},${y}`).join(" ");
  const last = s[s.length - 1] ?? 0;
  const gid = `g-${String(p.label).replace(/[^a-z0-9]/gi, "")}`;
  return (
    <div className="w-full max-w-6xl">
      <motion.div {...rise(0)} className="flex items-end justify-between mb-4">
        <span className="text-xl md:text-2xl text-white/80">{p.label}</span>
        <span className="font-serif text-5xl font-bold" style={{ color: p.color }}>${last.toLocaleString()}</span>
      </motion.div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        <defs><linearGradient id={gid} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={p.color} stopOpacity="0.45" /><stop offset="1" stopColor={p.color} stopOpacity="0" /></linearGradient></defs>
        {[0, 0.25, 0.5, 0.75, 1].map((k) => <line key={k} x1="40" x2={W - 20} y1={H - 30 - k * (H - 70)} y2={H - 30 - k * (H - 70)} stroke="rgba(255,255,255,0.08)" />)}
        <motion.path d={`${d} L${pts[pts.length - 1]?.[0]},${H - 30} L40,${H - 30} Z`} fill={`url(#${gid})`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2, delay: 2.5 }} />
        <motion.path d={d} fill="none" stroke={p.color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 5, delay: 0.6, ease: "easeInOut" }} style={{ filter: `drop-shadow(0 0 12px ${p.color})` }} />
        {all.map((_, i) => i % 4 === 0 && <text key={i} x={40 + (i * (W - 60)) / Math.max(all.length - 1, 1)} y={H - 6} fill="rgba(255,255,255,0.5)" fontSize="14" textAnchor="middle">{`Wk ${i + 1}`}</text>)}
      </svg>
    </div>
  );
}

export function Grid({ p, data }) {
  const weeks = num(p.weeks, data) || 12;
  const series = p.series ? resolve(p.series, data) : null;
  const filled = (w, r) => (series ? r < (series[w] || 0) : (w * 7 + r * 3) % 9 !== 0);
  return (
    <div className="w-full max-w-6xl space-y-2 overflow-hidden">
      {p.rows.map((row, r) => (
        <div key={row} className="grid items-center gap-3" style={{ gridTemplateColumns: `180px repeat(${weeks}, minmax(0,1fr))` }}>
          <span className="text-base md:text-lg text-white/80 truncate">{row}</span>
          {Array.from({ length: weeks }, (_, w) => (
            <motion.span key={w} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, delay: 0.5 + w * (6 / weeks) + r * 0.05 }}
              className="aspect-square rounded-md" style={{ background: filled(w, r) ? GOLD : "rgba(255,255,255,0.06)", boxShadow: filled(w, r) ? `0 0 14px ${GOLD}66` : "none" }} />
          ))}
        </div>
      ))}
      <div className="grid gap-3 pt-2" style={{ gridTemplateColumns: `180px repeat(${weeks}, minmax(0,1fr))` }}>
        <span />{Array.from({ length: weeks }, (_, w) => <span key={w} className="text-[10px] text-white/40 text-center">{(w + 1) % 4 === 0 ? w + 1 : ""}</span>)}
      </div>
    </div>
  );
}

export function Phases({ p, spoken = "" }) {
  const active = phaseSpoken(spoken, p.active);
  return (
    <div className="w-full max-w-6xl" data-testid="show-phases" data-active={active}>
      <div className="relative grid grid-cols-4 gap-4">
        <div className="absolute left-[12.5%] right-[12.5%] top-8 h-[3px] bg-white/10" />
        <motion.div initial={{ width: 0 }} animate={{ width: `${(Math.max(active - 1, 0) / 3) * 75}%` }} transition={{ duration: 1.6, ease }} className="absolute left-[12.5%] top-8 h-[3px]" style={{ background: GOLD, boxShadow: `0 0 20px ${GOLD}` }} />
        {p.items.map((t, i) => {
          const on = i < active;
          const now = i === active - 1;
          return (
            <motion.div key={t} {...rise(i, 0.5)} data-testid={`show-phase-${i + 1}`} data-on={on} className="relative flex flex-col items-center text-center gap-4">
              <motion.span animate={now ? { scale: [1, 1.2, 1] } : { scale: 1 }} transition={now ? { duration: 2, repeat: Infinity } : { duration: 0.5 }}
                className="w-16 h-16 rounded-full grid place-items-center font-serif text-2xl font-bold border-2 transition-colors duration-700" style={{ borderColor: on ? GOLD : "rgba(255,255,255,0.2)", background: now ? GOLD : on ? `${GOLD}33` : "#05070d", color: now ? "#000" : on ? "#fff" : "rgba(255,255,255,0.4)", boxShadow: now ? `0 0 50px ${GOLD}` : "none" }}>{i + 1}</motion.span>
              <span className={`text-lg md:text-2xl transition-colors duration-700 ${now ? "text-[#d4a017] font-semibold" : on ? "text-white" : "text-white/40"}`}>{t}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export function Phone({ p }) {
  return (
    <div className="flex items-center gap-12">
      <motion.div initial={{ opacity: 0, y: 80, rotateY: -25 }} animate={{ opacity: 1, y: 0, rotateY: -8 }} transition={{ duration: 1.4, ease }} style={{ perspective: 1200 }}
        className="relative w-[300px] h-[600px] max-h-[62vh] rounded-[48px] border-[10px] border-[#1c2233] bg-[#0b0814] shadow-[0_40px_120px_rgba(212,160,23,0.25)] overflow-hidden">
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-black" />
        <div className="pt-14 px-4 space-y-3">
          <div className="font-mono text-[10px] tracking-[0.3em]" style={{ color: GOLD }}>PACT LIFE PASS</div>
          {p.screens.map((s, i) => (
            <motion.div key={s} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 1 + i * 0.8, ease }}
              className={`rounded-xl p-4 border ${i === 0 ? "border-[#d4a017]/60 bg-[#d4a017]/10" : "border-white/10 bg-white/[0.04]"}`}>
              <div className="text-white text-[15px] font-semibold">{s}</div>
              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${40 + ((i * 23) % 55)}%` }} transition={{ duration: 1.2, delay: 1.4 + i * 0.8 }} className="h-full" style={{ background: GOLD }} /></div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export function Network({ p }) {
  const n = p.nodes.length;
  return (
    <div className="relative w-[640px] h-[460px] max-w-[92vw] max-h-[60vh]">
      <svg viewBox="-320 -230 640 460" className="absolute inset-0 w-full h-full">
        {p.nodes.map((_, i) => { const a = (i / n) * Math.PI * 2 - Math.PI / 2; return <motion.line key={i} x1="0" y1="0" x2={Math.cos(a) * 250} y2={Math.sin(a) * 175} stroke={GOLD} strokeOpacity="0.5" strokeWidth="2" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, delay: 0.8 + i * 0.35 }} />; })}
      </svg>
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 1, ease }} className="absolute left-1/2 top-1/2 -ml-16 -mt-16 w-32 h-32 rounded-full grid place-items-center font-serif text-xl text-black font-bold" style={{ background: GOLD, boxShadow: `0 0 80px ${GOLD}` }}>{p.center}</motion.div>
      {p.nodes.map((t, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        return <div key={t} className="absolute" style={{ left: `${50 + (Math.cos(a) * 250 * 100) / 640}%`, top: `${50 + (Math.sin(a) * 175 * 100) / 460}%`, transform: "translate(-50%,-50%)" }}>
          <motion.div initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 1.1 + i * 0.35, ease }} className="rounded-2xl border border-white/15 bg-[#0d1220]/90 backdrop-blur-xl px-5 py-3 text-white text-base md:text-lg whitespace-nowrap">{t}</motion.div>
        </div>;
      })}
    </div>
  );
}

export function Calendar({ p }) {
  return (
    <div className="grid grid-cols-5 gap-4 w-full max-w-6xl">
      {p.events.map(([d, t, l], i) => (
        <div key={d} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 min-h-[260px]">
          <div className="font-mono text-sm tracking-[0.3em] uppercase text-white/60">{d}</div>
          <motion.div initial={{ opacity: 0, y: -80 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.6 + i * 0.5 }}
            className="mt-6 rounded-xl p-4" style={{ background: `${GOLD}26`, borderLeft: `4px solid ${GOLD}` }}>
            <div className="font-mono text-sm" style={{ color: GOLD }}>{t}</div>
            <div className="text-white text-lg mt-1 leading-tight">{l}</div>
          </motion.div>
        </div>
      ))}
    </div>
  );
}
